'use client';

import { ChevronRightIcon } from '@c/icons';
import { LogoWithoutText } from '@c/icons/logoWithoutText';
import SafeImage from '@ui/SafeImage';
import { useConversationMutations } from '@util/hooks/useConversationMutations';
import { cn } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { ArchiveIcon } from 'i/chat/archive-icon';
import { MessageUnreadIcon } from 'i/chat/message-unread-icon';
import { ShareIcon, StarIcon, UserIcon, XIcon } from 'lucide-react';
import type { ChatDocument } from 'models/chat';
import type { PublicUserDocument, UserDocument } from 'models/user';
import Link from 'next/link';

export type ChatDetailsContentProps = {
  images?: string[];
  chat: ChatDocument;
  userDoc: UserDocument | null | undefined;
  sellerDoc: PublicUserDocument | null | undefined;
  buyerDoc: PublicUserDocument | null | undefined;
  onArchiveSuccess?: () => void;
  onDismiss: () => void;
  view: 'details' | 'gallery';
  setView: (view: 'details' | 'gallery') => void;
  onImageClick?: (imageUrl: string) => void;
};

export default function ChatDetailsContent({
  images = [],
  chat,
  sellerDoc,
  buyerDoc,
  onArchiveSuccess,
  onDismiss,
  view,
  setView,
  onImageClick,
}: ChatDetailsContentProps) {
  const { markAsUnread, archiveChat, starChat } = useConversationMutations();
  const { userDoc } = useAuth();
  const { showSuccessToast } = useToastContext();

  const onUnreadClick = () => {
    markAsUnread.mutate(chat.id);
  };

  const onStarClick = () => {
    starChat.mutate(chat.id);
    onDismiss();
  };

  const onArchiveClick = async () => {
    archiveChat.mutate(chat.id, {
      onSuccess: () => {
        onArchiveSuccess?.();
      },
    });
  };

  const onShareClick = () => {
    const link = `${window.location.origin}/dashboard/messages/${chat.id}`;
    navigator.clipboard.writeText(link).then(() => {
      showSuccessToast('Copied to clipboard');
    });
  };

  const otherPartyUser = chat.seller_id === userDoc?.uid ? buyerDoc : sellerDoc;

  if (view === 'gallery') {
    return (
      <div className="space-y-6">
        <div className="flex w-full items-center justify-between border-b border-zinc-200 px-8 pb-8 pt-2 sm:pt-8">
          <button
            type="button"
            onClick={() => setView('details')}
            className="duration-50 flex cursor-pointer items-center gap-2 text-[1.8rem] font-semibold text-brand-secondary transition-colors ease-linear hover:text-brand-secondary-darker"
          >
            Back
          </button>

          <XIcon className="h-10 w-10 cursor-pointer" onClick={onDismiss} />
        </div>

        <div className="px-8 py-6">
          <h3 className="text-[2.5rem] font-semibold">Gallery</h3>
        </div>

        <div className="grid h-[60rem] w-full grid-cols-3 gap-16 overflow-y-scroll px-8 pb-24 pt-4 sm:w-[50rem] sm:pt-0">
          {images.map((imageUrl, index) => (
            <button
              type="button"
              className="relative aspect-square cursor-pointer"
              key={`chat-image-${index}-${imageUrl.slice(-10)}`}
              onClick={() => onImageClick?.(imageUrl)}
            >
              <SafeImage
                src={imageUrl}
                alt={`Chat image ${index + 1}`}
                className="h-full w-full object-cover transition-transform duration-200 hover:scale-105"
              />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex max-h-[80vh] w-full flex-col gap-16 overflow-y-auto px-12 pb-12 md:w-[55rem]">
      <div className="space-y-6">
        <div className="flex w-full items-center justify-between">
          <h3 className="text-[2.5rem] font-semibold">Gallery</h3>

          {images.length > 0 && (
            <button
              type="button"
              onClick={() => setView('gallery')}
              className="text-[1.8rem] font-semibold text-brand-secondary"
            >
              See all
            </button>
          )}
        </div>

        {images.length > 0 ? (
          <div className="relative flex h-[9rem] w-full gap-4 overflow-x-hidden">
            {images.length > 0 &&
              images.map((imageUrl, index) => (
                <SafeImage
                  key={`chat-image-${index}-${imageUrl.slice(-10)}`}
                  src={imageUrl}
                  alt={`Chat image ${index + 1}`}
                  className="relative h-[9rem] w-[9rem] flex-shrink-0 overflow-hidden rounded-lg object-cover transition-transform duration-200"
                />
              ))}

            {images.length > 0 && (
              <div className="absolute right-0 h-full">
                <div className="h-full w-[30rem] bg-gradient-to-l from-white to-transparent" />
              </div>
            )}
          </div>
        ) : (
          <p className="text-[1.8rem] text-zinc-500">This chat has no images</p>
        )}
      </div>

      <div className="w-full space-y-6">
        <MessageAction
          icon={<UserIcon className="h-12 w-12" />}
          hideBottomBorder
          text="View User Profile"
          onClick={() => {
            window.open(`/profile/${otherPartyUser?.username_slug}`);
          }}
        />

        <h3 className="text-[2.5rem] font-semibold">Message Actions</h3>

        <div className="flex w-full flex-col">
          <MessageAction
            icon={<MessageUnreadIcon className="h-12 w-12" />}
            text="Mark as Unread"
            onClick={onUnreadClick}
          />

          <MessageAction
            icon={
              <StarIcon
                className={cn(
                  'h-12 w-12',
                  chat.favorited?.includes(userDoc?.uid ?? '') &&
                    'fill-black text-black'
                )}
              />
            }
            text="Star chat"
            onClick={onStarClick}
            isActive={chat.favorited?.includes(userDoc?.uid ?? '')}
            activeText="Unstar chat"
          />

          <MessageAction
            icon={<ShareIcon className="h-12 w-12" />}
            text="Share chat"
            onClick={onShareClick}
          />

          <MessageAction
            icon={<ArchiveIcon className="h-12 w-12" />}
            text="Delete"
            onClick={onArchiveClick}
          />
        </div>
      </div>

      <div className="w-full space-y-6">
        <h3 className="text-[2.5rem] font-semibold">Support</h3>

        <MessageAction
          icon={
            <div className="h-[5rem] w-[5rem] rounded-full border border-zinc-200 p-4">
              <LogoWithoutText className="h-12 w-12" fill="#444444" />
            </div>
          }
          hideBottomBorder
          text="Visit the Help Center"
          onClick={() => {
            window.open('/help', '_blank');
          }}
        />
      </div>

      <div className="w-full space-y-6">
        <h3 className="text-[2.5rem] font-semibold">Things to keep in mind</h3>

        <p className="text-[1.8rem] font-medium text-zinc-600">
          We analyze messages for safety, support, and to provide and improve
          our services.{' '}
          <Link href="/privacy-policy" className="font-bold underline">
            Learn more
          </Link>
        </p>

        <p className="text-[1.8rem] font-medium text-zinc-600">
          To protect your payment, always communicate and pay through the MX
          Locker website or app.{' '}
          <Link
            href="/buyer-and-seller-protection"
            className="font-bold underline"
          >
            Learn more
          </Link>
        </p>
      </div>
    </div>
  );
}

const MessageAction = ({
  icon,
  text,
  onClick,
  hideBottomBorder = false,
  isActive = false,
  activeText,
}: {
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
  hideBottomBorder?: boolean;
  isActive?: boolean;
  activeText?: string;
}) => {
  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-center gap-10 border-b border-[#EDEDED] p-10 px-4 transition-all duration-200 hover:bg-zinc-50 hover:shadow-sm active:bg-zinc-100',
        hideBottomBorder && 'border-b-0'
      )}
      onClick={onClick}
    >
      {icon}

      <div className="flex w-full items-center justify-between">
        <span className="text-[2.1rem] font-medium">
          {isActive ? activeText : text}
        </span>

        <ChevronRightIcon className="h-10 w-10 text-zinc-600 transition-transform duration-200 group-hover:translate-x-1" />
      </div>
    </button>
  );
};
